import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Alert, IconButton, Chip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { environmentsAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { Environment } from '../../types';

interface EnvironmentFormData {
  environmentName: string;
  environmentCode: string;
  vmDiscountMultiplier: number;
  databaseVMDiscountMultiplier: number;
  diskDiscountMultiplier: number;
  standardStorageGB: number;
  displayOrder: number;
  isActive: boolean;
  enabledForStandardVMs: boolean;
  enabledForIBMPower: boolean;
}

const Environments = () => {
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEnv, setEditingEnv] = useState<Environment | null>(null);
  const [formData, setFormData] = useState<EnvironmentFormData>({
    environmentName: '', environmentCode: '', vmDiscountMultiplier: 1.0, databaseVMDiscountMultiplier: 1.0, diskDiscountMultiplier: 1.0, standardStorageGB: 128, displayOrder: 999, isActive: true,
    enabledForStandardVMs: true, enabledForIBMPower: true,
  });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchEnvironments(); }, []);

  const fetchEnvironments = async () => {
    try { setLoading(true); const response = await environmentsAPI.getAll(); setEnvironments(response.data); }
    catch (err) { setError('Failed to load environments'); } finally { setLoading(false); }
  };

  const handleOpenDialog = (env: Environment | null = null) => {
    if (env) {
      setEditingEnv(env);
      setFormData({
        environmentName: env.EnvironmentName, environmentCode: env.EnvironmentCode,
        vmDiscountMultiplier: env.VMDiscountMultiplier, databaseVMDiscountMultiplier: env.DatabaseVMDiscountMultiplier, diskDiscountMultiplier: env.DiskDiscountMultiplier,
        standardStorageGB: env.StandardStorageGB, displayOrder: env.DisplayOrder, isActive: !!env.IsActive,
        enabledForStandardVMs: env.EnabledForStandardVMs !== 0, enabledForIBMPower: env.EnabledForIBMPower !== 0,
      });
    } else {
      setEditingEnv(null);
      setFormData({ environmentName: '', environmentCode: '', vmDiscountMultiplier: 1.0, databaseVMDiscountMultiplier: 1.0, diskDiscountMultiplier: 1.0, standardStorageGB: 128, displayOrder: 999, isActive: true, enabledForStandardVMs: true, enabledForIBMPower: true });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => { setDialogOpen(false); setEditingEnv(null); setError(''); };

  const handleSave = async () => {
    try {
      if (editingEnv) { await environmentsAPI.update(editingEnv.EnvironmentId, formData); setSuccess('Environment updated successfully'); }
      else { await environmentsAPI.create(formData); setSuccess('Environment created successfully'); }
      handleCloseDialog(); fetchEnvironments(); setTimeout(() => setSuccess(''), 3000);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to save environment');
    }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const envId = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try { const response = await environmentsAPI.delete(envId); setSuccess(response.data.message); fetchEnvironments(); setTimeout(() => setSuccess(''), 3000); }
    catch (err) { setError('Failed to delete environment'); }
  };

  const handleChange = (field: string, value: string | number | boolean) => { setFormData({ ...formData, [field]: value }); };
  const formatPercent = (multiplier: number) => `${Math.round(multiplier * 100)}%`;

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>Environments</Typography>
          <Typography variant="body2" color="text.secondary">Manage deployment environments with different VM pricing discounts and configurations</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>Add Environment</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      <Paper sx={{ p: 0 }}>
        <TableContainer>
          <Table>
            <TableHead><TableRow>
              <TableCell>Name</TableCell><TableCell>Code</TableCell><TableCell align="center">VM Discount</TableCell><TableCell align="center">DB VM Discount</TableCell><TableCell align="center">Disk Discount</TableCell><TableCell align="center">Standard Storage</TableCell><TableCell align="center">Std VMs</TableCell><TableCell align="center">IBM Power</TableCell><TableCell align="center">Order</TableCell><TableCell align="center">Status</TableCell><TableCell align="center">Actions</TableCell>
            </TableRow></TableHead>
            <TableBody>
              {environments.map((env) => (
                <TableRow key={env.EnvironmentId} hover>
                  <TableCell>{env.EnvironmentName}</TableCell>
                  <TableCell><Chip label={env.EnvironmentCode} size="small" /></TableCell>
                  <TableCell align="center">{formatPercent(env.VMDiscountMultiplier)}</TableCell>
                  <TableCell align="center">{formatPercent(env.DatabaseVMDiscountMultiplier)}</TableCell>
                  <TableCell align="center">{formatPercent(env.DiskDiscountMultiplier)}</TableCell>
                  <TableCell align="center">{env.StandardStorageGB} GB</TableCell>
                  <TableCell align="center"><Chip label={env.EnabledForStandardVMs !== 0 ? 'Yes' : 'No'} color={env.EnabledForStandardVMs !== 0 ? 'success' : 'default'} size="small" /></TableCell>
                  <TableCell align="center"><Chip label={env.EnabledForIBMPower !== 0 ? 'Yes' : 'No'} color={env.EnabledForIBMPower !== 0 ? 'success' : 'default'} size="small" /></TableCell>
                  <TableCell align="center">{env.DisplayOrder}</TableCell>
                  <TableCell align="center"><Chip label={env.IsActive ? 'Active' : 'Inactive'} color={env.IsActive ? 'success' : 'default'} size="small" /></TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenDialog(env)} color="primary"><EditIcon fontSize="small" /></IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(env.EnvironmentId)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>How environments work:</strong><br />
          • <strong>VM Discount:</strong> Applied to base VM or Power VM cost only (not disks or addons). 100% = full price, 70% = 30% discount<br />
          • <strong>Disk Discount:</strong> Applied to storage costs for standard VMs, database VMs, and IBM Power VMs. 100% = full price, 70% = 30% discount<br />
          • <strong>Enabled for Standard VMs:</strong> Whether this environment appears in the dropdown for standard VM and Database VM rows<br />
          • <strong>Enabled for IBM Power:</strong> Whether this environment appears in the IBM Power VM dropdown<br />
          • <strong>Standard Storage:</strong> Informational text shown in quotes<br />
          • <strong>Display Order:</strong> Determines the order environments appear in dropdowns<br />
          • <strong>Default Addons:</strong> Configure separately per environment on the Default Addons page
        </Typography>
      </Alert>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingEnv ? 'Edit Environment' : 'Add New Environment'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Environment Name" value={formData.environmentName} onChange={(e) => handleChange('environmentName', e.target.value)} required fullWidth helperText="e.g., Production, Development, Staging" />
            <TextField label="Environment Code" value={formData.environmentCode} onChange={(e) => handleChange('environmentCode', e.target.value)} required fullWidth helperText="Short code (e.g., prod, dev, stg)" />
            <TextField label="VM Discount Multiplier" type="number" value={formData.vmDiscountMultiplier} onChange={(e) => handleChange('vmDiscountMultiplier', parseFloat(e.target.value))} required fullWidth inputProps={{ min: 0, max: 1, step: 0.01 }} helperText="1.0 = 100% (full price), 0.7 = 70% (30% discount)" />
            <TextField label="Database VM Discount Multiplier" type="number" value={formData.databaseVMDiscountMultiplier} onChange={(e) => handleChange('databaseVMDiscountMultiplier', parseFloat(e.target.value))} required fullWidth inputProps={{ min: 0, max: 1, step: 0.01 }} helperText="1.0 = 100% (full price), 0.7 = 70% (30% discount)" />
            <TextField label="Disk Discount Multiplier" type="number" value={formData.diskDiscountMultiplier} onChange={(e) => handleChange('diskDiscountMultiplier', parseFloat(e.target.value))} required fullWidth inputProps={{ min: 0, max: 1, step: 0.01 }} helperText="Applied to storage costs for standard VMs, database VMs, and IBM Power VMs. 1.0 = 100% (full price), 0.7 = 70% (30% discount)" />
            <TextField label="Standard Storage (GB)" type="number" value={formData.standardStorageGB} onChange={(e) => handleChange('standardStorageGB', parseInt(e.target.value))} required fullWidth helperText="Informational only - shown in quote headers" />
            <TextField label="Display Order" type="number" value={formData.displayOrder} onChange={(e) => handleChange('displayOrder', parseInt(e.target.value))} required fullWidth helperText="Lower numbers appear first in dropdowns" />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input type="checkbox" checked={formData.isActive} onChange={(e) => handleChange('isActive', e.target.checked)} />
                <Typography variant="body2">Active (show in dropdowns)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input type="checkbox" checked={formData.enabledForStandardVMs} onChange={(e) => handleChange('enabledForStandardVMs', e.target.checked)} />
                <Typography variant="body2">Enabled for Standard VMs (show in VM &amp; Database VM environment dropdown)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input type="checkbox" checked={formData.enabledForIBMPower} onChange={(e) => handleChange('enabledForIBMPower', e.target.checked)} />
                <Typography variant="body2">Enabled for IBM Power (show in IBM Power VM environment dropdown)</Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">{editingEnv ? 'Update' : 'Create'}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Deactivate Environment"
        message="Are you sure you want to deactivate this environment?"
        confirmLabel="Deactivate"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </Box>
  );
};

export default Environments;
